import { Fragment, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

import { FaTimes } from 'react-icons/fa';

import './Modal.css';
import Button from '../button/Button';
import { DynamicIcon } from '../dynamicIcon';

import { useOnClickOutside } from 'hooks';
import { useAutoOpen } from './hooks';

const Modal = ({
    animation = 'fade-in',
    backgroundColor,
    color,
    icon,
    variant,
    placement,
    width,
    renderContent,
    className,
    isOpen,
    onClose,
    onOpen,
    styles,
    title,
    onOverlayClick,
    autoOpen: autoOpenConditions = {},
    showHeader = true,
    extraBodyClass,
    closeAutoSeconds,
}) => {
    const modalRef = useRef();
    onOverlayClick && useOnClickOutside(modalRef, onOverlayClick);

    const commonStyles =
        variant === 'secondary'
            ? {
                  backgroundColor,
                  color,
              }
            : {};

    const { removeAutoOpenEventHandlers } = useAutoOpen(
        autoOpenConditions,
        onOpen
    );

    const handleClose =
        typeof onClose === 'function'
            ? () => {
                  removeAutoOpenEventHandlers();
                  onClose();
              }
            : undefined;

    if (closeAutoSeconds) {
        const autoClose = () => {
            onOverlayClick();
        };

        useEffect(() => {
            const id = setInterval(autoClose, closeAutoSeconds * 1000);
            return () => clearInterval(id);
        }, []);
    }

    return (
        <CSSTransition
            in={isOpen}
            timeout={300}
            nodeRef={modalRef}
            unmountOnExit
            classNames={`ab-${animation}`}
        >
            <Fragment>
                {ReactDOM.createPortal(
                    <div className="ab-modal-scroll-overlay">
                        <div className={`ab-clickable-overlay--${placement}`}>
                            <div
                                className={`ab-modal ab-modal--${placement} ab-modal--${variant} ${className}`}
                                style={{
                                    maxWidth: width + 'px',
                                    backgroundColor:
                                        commonStyles?.backgroundColor,
                                }}
                                ref={modalRef}
                            >
                                {showHeader && (
                                    <div
                                        className={`ab-modal-header ab-modal-header--${variant}`}
                                        style={commonStyles}
                                    >
                                        <Button
                                            className="ab-modal-close-icon"
                                            onClick={handleClose}
                                            color={styles?.closeIcon?.color}
                                        >
                                            <FaTimes />
                                        </Button>
                                        {icon && variant === 'secondary' && (
                                            <span
                                                className="ab-modal-header-icon"
                                                style={{
                                                    color,
                                                    fontSize: title
                                                        ? '30px'
                                                        : '50px',
                                                }}
                                            >
                                                <DynamicIcon name={icon} />
                                            </span>
                                        )}
                                        {title && (
                                            <h3
                                                className="ab-modal-header-title"
                                                style={
                                                    variant === 'secondary'
                                                        ? { color }
                                                        : {}
                                                }
                                            >
                                                {title}
                                            </h3>
                                        )}
                                    </div>
                                )}

                                <div
                                    className={`ab-modal-body ${extraBodyClass}`}
                                >
                                    {renderContent()}
                                </div>
                            </div>
                        </div>
                    </div>,
                    $modalRoot
                )}
            </Fragment>
        </CSSTransition>
    );
};

const $modalRoot = document.getElementById('abouthotelier-modal-root');

Modal.propTypes = {
    animation: PropTypes.oneOf([
        'fade-in',
        'slide-in-bottom',
        'slide-in-top',
        'slide-in-left',
        'slide-in-right',
    ]),
    autoOpen: PropTypes.shape({
        clickCount: PropTypes.number,
        pageScroll: PropTypes.number,
        timeDelay: PropTypes.number,
    }),
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string,
    placement: PropTypes.oneOf(['center', 'aside', 'top']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'white-header']),
    width: PropTypes.number,
    renderContent: PropTypes.func.isRequired,
    className: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    styles: PropTypes.object,
    title: PropTypes.string,
    onOverlayClick: PropTypes.func,
    showHeader: PropTypes.bool,
    extraBodyClass: PropTypes.string,
    closeAutoSeconds: PropTypes.number,
};

Modal.defaultProps = {
    color: '#fff',
    backgroundColor: '#222',
    icon: '',
    placement: 'center',
    variant: 'primary',
    width: 600,
    renderLink: () => {},
    className: undefined,
    isOpen: false,
    styles: {},
};

export default Modal;
